import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys, unwrap, type components } from '@geefamily/api';
import { api } from '@/app/api';
import { useAuth } from '@/features/auth/use-auth';

export type MarketProduct = components['schemas']['MarketProductResponse'];
export type GeeCoinAccount = components['schemas']['GeeCoinAccountResponse'];
export type GeeCoinTransactions = components['schemas']['GeeCoinTransactionsResponse'];
export type MarketOrder = components['schemas']['MarketOrderResponse'];
export type MarketOrders = components['schemas']['MarketOrdersResponse'];
export type CheckoutRequest = components['schemas']['CreateMarketOrderRequest'];
const keys = queryKeys.module('market');
const products = keys.entity('products');
const members = keys.entity('me');

export function useMarketProducts() {
  return useQuery({
    queryKey: products.lists(),
    queryFn: () => unwrap<MarketProduct[]>(api.GET('/api/v1/market/products', {})),
    staleTime: 15_000,
  });
}

/** User identity is part of every private cache key, including during session changes. */
export function useGeeCoinAccount() {
  const { isAuthenticated, user } = useAuth();
  return useQuery({
    queryKey: [...members.detail(user?.id ?? ''), 'account'],
    queryFn: () => unwrap<GeeCoinAccount>(api.GET('/api/v1/market/me/account', {})),
    enabled: isAuthenticated && Boolean(user),
    staleTime: 15_000,
  });
}

export function useGeeCoinTransactions(page: number) {
  const { isAuthenticated, user } = useAuth();
  return useQuery({
    queryKey: [...members.detail(user?.id ?? ''), 'transactions', page],
    queryFn: () =>
      unwrap<GeeCoinTransactions>(
        api.GET('/api/v1/market/me/transactions', {
          params: { query: { page, pageSize: 10 } },
        }),
      ),
    enabled: isAuthenticated && Boolean(user),
  });
}

export function useMarketOrders(page: number) {
  const { isAuthenticated, user } = useAuth();
  return useQuery({
    queryKey: [...members.detail(user?.id ?? ''), 'orders', page],
    queryFn: () =>
      unwrap<MarketOrders>(
        api.GET('/api/v1/market/me/orders', {
          params: { query: { page, pageSize: 6 } },
        }),
      ),
    enabled: isAuthenticated && Boolean(user),
  });
}

export function useMarketCheckout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CheckoutRequest) =>
      unwrap<MarketOrder>(api.POST('/api/v1/market/orders', { body })),
    retry: false,
    onSettled: () => client.invalidateQueries({ queryKey: keys.all }),
  });
}
